import { useState, useEffect } from 'react'
import { MinimalLayout } from '@/components/layouts/MinimalLayout'
import { EditorNavbar } from '@/components/nav/EditorNavbar'
import { WebWorkerDebugger } from '@/components/core/WebWorkerDebugger'
import { ViewWorkerStatus } from '@/components/core/ViewWorkerStatus'
import { ReadOnlyBanner } from '@/components/banners/ReadOnlyBanner'
import { CadPageContainer } from '@/components/layouts/CadPageContainer'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'

import { CADEditor } from './components/CADEditor/CADEditor'
import { AssemblyTree } from './components/AssemblyTree'
import { SidePanel } from './components/SidePanel'
import { DraggableTreeNodeContext } from './components/AssemblyTree/DraggableTreeNodeContext'
import { useCADPageListeners } from './hooks/useCADPageListeners'
import { useAssemblyPanelSize } from './hooks/useAssemblyPanelSize'
import { useInitialViewJobs } from './hooks/useInitialViewJobs'
import { usePreloadImages } from '@/utils/hooks/usePreloadImages'
import { useCADPageStore } from '@/state'
import { useCADVersionProcessing } from '@/services/hooks/cad_versions'
import { ToggleAssemblyTreeButton } from './components/ToggleAssemblyTreeButton'
import { CADVersionProcessingHelper } from './components/CadVersionProcessingHelper'
import { Spinner } from '@/components/ui/spinner'

export const CADPage = () => {
  usePreloadImages()
  useCADPageListeners()
  const [showWebWorkerDebugger, setShowWebWorkerDebugger] =
    useState<boolean>(false)
  const [showViewWorkerStatus, setShowViewWorkerStatus] =
    useState<boolean>(false)

  useEffect(() => {
    // @ts-expect-error - adding react-query devtools toggle
    window.toggleWebWorkerDebugger = () =>
      setShowWebWorkerDebugger((old) => !old)
    // @ts-expect-error - adding view worker status toggle
    window.toggleViewWorkerStatus = () => setShowViewWorkerStatus((old) => !old)
  }, [])

  const assemblyPanelSize = useAssemblyPanelSize()
  const isAssemblyTreeOpen = useCADPageStore(
    (state) => state.isAssemblyTreeOpen,
  )

  const {
    isLoading,
    isComplete,
    isProcessing,
    data: cadVersionProcess,
  } = useCADVersionProcessing({})

  useInitialViewJobs({ isComplete })

  return (
    <MinimalLayout>
      <EditorNavbar active="cad" />
      <ReadOnlyBanner />
      <CadPageContainer>
        {isLoading && <Spinner />}
        {isComplete && (
          <ResizablePanelGroup direction="horizontal" className="py-4 px-2">
            <DraggableTreeNodeContext>
              <ResizablePanel
                defaultSize={assemblyPanelSize}
                maxSize={Infinity}
                className="pr-1"
                style={{
                  minWidth: isAssemblyTreeOpen ? 320 : 30,
                  maxWidth: isAssemblyTreeOpen ? undefined : 30,
                }}
              >
                {isAssemblyTreeOpen ? (
                  <AssemblyTree />
                ) : (
                  <div className="mt-6">
                    <ToggleAssemblyTreeButton />
                  </div>
                )}
              </ResizablePanel>
              {isAssemblyTreeOpen && <ResizableHandle withHandle />}
              <ResizablePanel
                className="pl-4 pt-2"
                style={{ width: 320, maxWidth: 320, minWidth: 320 }}
              >
                <SidePanel />
              </ResizablePanel>
            </DraggableTreeNodeContext>
            <ResizablePanel className="pt-2">
              <CADEditor />
              {showWebWorkerDebugger && <WebWorkerDebugger />}
              {showViewWorkerStatus && <ViewWorkerStatus />}
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
        {isProcessing && (
          <CADVersionProcessingHelper
            cadVersionId={cadVersionProcess.cadVersionId}
            status={cadVersionProcess.processingStatus}
            hasNoDownloadUrl={cadVersionProcess.hasNoDownloadUrl}
          />
        )}
      </CadPageContainer>
    </MinimalLayout>
  )
}
